import { isEqual } from 'lodash';
import React, { useEffect, useState } from 'react';

import {
  deleteCrmIntegration,
  getCrmIntegrationLoginUrl,
  getCrmIntegrationLoginUrlFromEvi2ken,
} from '~/api/crms';
import { getCrmIntegrations } from '~/common/constants/integrations';
import logos from '~/components/Shared/ProviderLogo/logos';
import { Button } from '~/eds/src/components/Button/Button';
import { FeatureFlagType } from '~/enums/FeatureFlagType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useAsync } from '~/hooks/useAsync';
import { useHasFeatureFlag } from '~/hooks/useHasFeatureFlag';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRouting } from '~/routing/useRouting';
import Card from '~/ui/components/Card/index';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Link from '~/ui/components/Link/Link';
import Text from '~/ui/components/Text/Text';

import IntegrationDetailCard from '../../IntegrationDetailCard';
import ContactIntegrationMessage from '../ContactIntegrationMessage';
import StatusAndActions from './StatusAndActions';

function CrmIntegrationCard({
  disabled = false,
  crmIntegration,
  isRefetching,
  onRefetch,
}) {
  const intl = useIntl();
  const { navigate, params } = useRouting();
  const [draft, setDraft] = useState(crmIntegration);
  const [isCollapsed, setIsCollapsed] = useState(true);

  const { provider: type } = crmIntegration;
  const crmIntegrations = getCrmIntegrations();
  const { name, provider, title } = crmIntegrations[type];
  const logoSrc = logos[provider];

  const hasApiIntegration = useHasFeatureFlag(FeatureFlagType.ExternalApi);

  const isUsingEvi2ken = useFlag(FlagType.MultipleSfdcMigrationToEvi2ken);

  useEffect(() => {
    setDraft(crmIntegration);
  }, [crmIntegration]);

  const { executor: fetchIntegrationLoginUrl } = useAsync(
    isUsingEvi2ken
      ? getCrmIntegrationLoginUrlFromEvi2ken
      : getCrmIntegrationLoginUrl,
    {
      integrationUrl: window.location.href,
    },
    {
      errorToastMessage: intl.formatMessage({
        id: 'CrmIntegrationCard.fetchLoginUrlError',
        defaultMessage:
          'Unable to fetch CRM integration login url, please try again.',
        description: 'Error message for fetch login URL failure',
      }),
      successHandler: ({ redirectUrl }) => (window.location.href = redirectUrl),
    },
  );

  const { executor: executeDeleteIntegration, isLoading: isDeleting } =
    useAsync(deleteCrmIntegration, crmIntegration, {
      errorToastMessage: intl.formatMessage({
        id: 'CrmIntegrationCard.deleteError',
        defaultMessage: 'Unable to delete CRM integration, please try again',
        description: 'Error message for delete integration failure',
      }),
      successHandler: onRefetch,
    });

  const isUnchanged = isEqual(draft, crmIntegration);
  const isLoading = isRefetching || isDeleting;

  function handleClose() {
    setIsCollapsed(true);
  }

  function handleConnect() {
    setIsCollapsed(false);
  }

  const headerRightContent = (
    <StatusAndActions
      disabled={disabled}
      integration={crmIntegration}
      isActive={!isCollapsed}
      isLoading={isLoading}
      isPending={!isUnchanged}
      onConnect={handleConnect}
      onDelete={executeDeleteIntegration}
      onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
    />
  );

  const footer = (
    <Card.Footer
      actionButtons={[
        <Button
          key="Close"
          disabled={isLoading}
          text={intl.formatMessage({
            id: 'CrmIntegrationCard.close',
            defaultMessage: 'Close',
            description: 'Close button text',
          })}
          onClick={handleClose}
          variant="tertiary"
        />,
      ]}
    />
  );

  const disabledFeatureDescription = (
    <FlexLayout flexDirection="column" space={6}>
      <Text color="gray-800" variant="s-dense">
        {intl.formatMessage({
          id: 'CrmIntegrationCard.salesforceDescription1',
          defaultMessage:
            'Combine Evisort and Salesforce to allow legal and sales teams to collaborate more efficiently and effectively, creating true visibility into your contracts and providing key data to your sales team.',
          description: 'First part of Salesforce integration description',
        })}
      </Text>
      <Text color="gray-800" variant="s-dense">
        {intl.formatMessage({
          id: 'CrmIntegrationCard.salesforceDescription2',
          defaultMessage: 'Evisort enables a two-way integration that:',
          description: 'Second part of Salesforce integration description',
        })}
      </Text>
      <Text color="gray-800" variant="s-dense">
        {intl.formatMessage({
          id: 'CrmIntegrationCard.salesforceDescription3',
          defaultMessage:
            '(1) allows Salesforce users to initiate contract requests and monitor their statuses from Salesforce,',
          description: 'Third part of Salesforce integration description',
        })}
      </Text>
      <Text color="gray-800" variant="s-dense">
        {intl.formatMessage({
          id: 'CrmIntegrationCard.salesforceDescription4',
          defaultMessage:
            '(2) automatically ingests and analyzes contracts from Salesforce; extracted contract clauses, fields, and metadata can be pulled back to Salesforce for analysis, dashboarding, and alerts.',
          description: 'Fourth part of Salesforce integration description',
        })}
      </Text>
    </FlexLayout>
  );

  return (
    <IntegrationDetailCard
      isCollapsed={isCollapsed}
      footer={footer}
      headerRightContent={headerRightContent}
      logoSrc={logoSrc}
      title={title || name}
      onToggleCollapse={setIsCollapsed}
    >
      <FlexLayout alignItems="stretch" flexDirection="row" space={6}>
        {hasApiIntegration && (
          <FlexLayout flexDirection="column" space={8}>
            <Text color="gray-800" variant="s-dense" isReadable>
              <FormattedMessage
                id="CrmIntegrationCard.part1Instructions"
                defaultMessage="Part 1: Generate an Evisort API Key to use in your Salesforce configuration (configuration guide can be found <configLink>here</configLink>). <apiLink>Go to Evisort API Management</apiLink>."
                description="Instructions for part 1 of Salesforce integration"
                values={{
                  configLink: (chunks) => (
                    <Link href="https://support.evisort.com/hc/en-us/articles/360060522013-Salesforce-Integration-How-To-Install-the-Evisort-Salesforce-Package-from-the-AppExchange">
                      {chunks}
                    </Link>
                  ),
                  apiLink: (chunks) => (
                    <Link
                      onClick={() =>
                        navigate(
                          `/admin/console/${params.clientId}/integrations?tab=api-management`,
                        )
                      }
                    >
                      {chunks}
                    </Link>
                  ),
                }}
              />
            </Text>
            <Text color="gray-800" variant="s-dense" isReadable>
              <FormattedMessage
                id="CrmIntegrationCard.part2Instructions"
                defaultMessage="Part 2: Log into your Salesforce account to authenticate and enable the integration. <authLink>Authenticate your account</authLink>."
                description="Instructions for part 2 of Salesforce integration"
                values={{
                  authLink: (chunks) => (
                    <Link onClick={fetchIntegrationLoginUrl}>{chunks}</Link>
                  ),
                }}
              />
            </Text>
          </FlexLayout>
        )}
        {!hasApiIntegration && (
          <ContactIntegrationMessage
            description={disabledFeatureDescription}
            enableIcon={true}
            isUnpurchased={true}
          />
        )}
      </FlexLayout>
    </IntegrationDetailCard>
  );
}

export default CrmIntegrationCard;
